<template>
    <layout>
        <main role="main" class="background-employer-dashboard background-employer-dashboard-bottom pb-5">
            <div class="container">
                <nav aria-label="breadcrumb" class="mt-4">
                    <ol class="breadcrumb font-poppins font-weight-light font-size-14 color-blue px-0 bg-white">
                        <li class="breadcrumb-item">
                            <a :href="route('nl.employerdashboard.invoices.overview', $page.props.auth.user.selected_company.uuid)"
                                class="font-poppins font-weight-semibold color-blue">
                                {{ Lang.get('employerdashboard::pages/invoice/details.breadcrumb.invoices_overview') }}
                            </a>
                        </li>
                        <li class="breadcrumb-item color-blue" aria-current="page">
                            {{ Lang.get('employerdashboard::pages/invoice/details.breadcrumb.invoice_details', {number: invoice.number}) }}
                        </li>
                    </ol>
                </nav>
                <h1 class="font-poppins font-weight-extra-light color-blue mt-4">
                    {{ Lang.get('employerdashboard::pages/invoice/details.title', {number: invoice.number}) }}</h1>
                <h6 class="font-poppins font-weight-light font-size-18 color-blue">
                    {{ Lang.get('employerdashboard::pages/invoice/details.subtitle') }}</h6>
                <div class="row mt-5">
                    <div class="col-12 col-md-8 mb-4">
                        <div class="card border-radius-8 shadow">
                            <div class="card-body">
                                <div class="card border-0" v-for="period in invoice_periods">
                                    <input type="checkbox" hidden @change="loadFirstBatchOfTransactions(period)" :id="`period_dropdown_${period}`">
                                    <label class="card-header bg-white border-bottom d-flex justify-content-between align-items-center cursor-pointer"
                                         :for="`period_dropdown_${period}`"
                                         style="padding-left: 1vw; padding-bottom: 1.5vh;">
                                        <span class="font-poppins font-size-18 color-blue">
                                            {{ Lang.get('employerdashboard::pages/invoice/details.table.period') }}:
                                            {{ period }}
                                        </span>
                                        <img src="/img/employer-dashboard/icons/chevron-right.svg">
                                    </label>
                                    <div class="card border-0">
                                        <div class="card-header bg-white font-poppins color-blue collapsed border-bottom">
                                            <div class="row">
                                                <div class="col">
                                                    {{ Lang.get('employerdashboard::pages/invoice/details.table.reference') }}
                                                </div>
                                                <div class="col">
                                                    {{ Lang.get('employerdashboard::pages/invoice/details.table.type_of_transaction') }}
                                                </div>
                                                <div class="col">
                                                    {{ Lang.get('employerdashboard::pages/invoice/details.table.created_at') }}
                                                </div>
                                                <div class="col">
                                                    {{ Lang.get('employerdashboard::pages/invoice/details.table.amount') }}
                                                </div>
                                                <div class="col">
                                                    {{ Lang.get('employerdashboard::pages/invoice/details.table.employee') }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="card border-0" v-for="transaction in transactionsByPeriod[period]">
                                        <div class="card-header bg-white color-blue font-size-12">
                                            <div class="row">
                                                <div class="col">{{ transaction.reference }}</div>
                                                <div class="col">{{ transaction.method }}</div>
                                                <div class="col">{{ transaction.created_at }}</div>
                                                <div class="col">{{ transaction.total_amount_incl | toCurrency }}</div>
                                                <div class="col">
                                                    <inertia-link :href="route('nl.employerdashboard.employee.profile', {
                                                                    company_as_uuid: $page.props.auth.user.selected_company.uuid,
                                                                    employee_uuid: transaction.employee.uuid
                                                                })" class="font-poppins color-blue">
                                                        {{ transaction.employee.full_name }}
                                                    </inertia-link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="card border-0" v-if="transactionsMetaByPeriod[period]">
                                        <div class="card-header bg-white color-blue font-size-12 text-center">
                                            <p v-if="transactionsByPeriod[period] && (transactionsMetaByPeriod[period].total || 0) > transactionsByPeriod[period].length">
                                                {{ Lang.get('employerdashboard::pages/invoice/details.table.number_of_transactions_not_shown', {number: ((transactionsMetaByPeriod[period].total || 0) - transactionsByPeriod[period].length).toString()}) }}
                                                <br>
                                                <a role="button" @click="loadNextBatchOfTransactions(period)" class="btn btn-link">
                                                    {{ Lang.get('employerdashboard::pages/invoice/details.table.load_more') }}
                                                </a>
                                            </p>
                                            <p v-if="transactionsMetaByPeriod[period].error" class="alert alert-danger">
                                                {{ Lang.get('employerdashboard::pages/invoice/details.table.load_more_error') }}
                                            </p>
                                            <p v-if="transactionsMetaByPeriod[period].loading">
                                                <span class="fa fa-spinner fa-spin fa-2x color-blue"></span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-md-4 mb-2">
                        <div class="card border-radius-8 shadow">
                            <div class="card-body">
                                <div class="row">
                                    <div class="col-12 mb-4">
                                        <h1>
                                            {{ Lang.get('employerdashboard::pages/invoice/details.widgets.details.title') }}
                                        </h1>

                                        <span class="font-weight-semibold font-poppins color-dark-blue-light">
                                            {{ Lang.get('employerdashboard::pages/invoice/details.widgets.details.status') }}
                                        </span>
                                        <br/>
                                        <span class="badge font-poppins font-size-14 font-weight-semibold px-4 py-2"
                                              :class="'badge-' + invoice_status.label"
                                              v-html="invoice_status.text"></span>
                                    </div>

                                    <div class="col-12 mb-4 color-blue" v-if="file_format">
                                        <img src="/img/employer-dashboard/icon-download.svg">
                                        <a :href="route('nl.employerdashboard.invoice.download', {
                                        company_as_uuid: $page.props.auth.user.selected_company.uuid,
                                        invoice: invoice.uuid
                                        })">
                                            <span class="ml-1 font-weight-semibold font-poppins">
                                                {{ Lang.get('employerdashboard::pages/invoice/details.invoice.download', {file_format:file_format}) }}
                                            </span> </a>
                                    </div>
                                </div>

                                <div class="row" v-if="!invoice_is_paid && how_to_pay_description">
                                    <div class="col-12 mb-4">
                                        <h1>
                                            {{ Lang.get('employerdashboard::pages/invoice/details.widgets.details.pay') }}
                                        </h1>

                                        <div class="alert alert-default">
                                            <span class="color-dark-blue font-weight-semibold">
                                                {{ Lang.get('employerdashboard::pages/invoice/details.widgets.details.how_to_pay.title') }}
                                            </span>
                                            <p class="color-dark-blue mt-2" v-html="how_to_pay_description"></p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    </layout>
</template>

<script>
import Layout from '@/Shared/Layout'
import TextInput from "@/Shared/TextInput";
import SelectInput from "@/Shared/SelectInput";
import axios from 'axios';

export default {
    metaInfo() {
        return {
            title: `${this.Lang.get('employerdashboard::pages/invoice/details.page_title', {'number': this.invoice.number})}`,
        }
    },
    components: {
        SelectInput,
        TextInput,
        Layout,
    },
    props: {
        invoice: Object,
        invoice_periods:Array,
        how_to_pay_description: String,
        invoice_status: Object,
        invoice_is_paid: Boolean,
        file_format: String
    },

    data() {
        return {
            transactionsByPeriod: {},
            transactionsMetaByPeriod: {},
        }
    },
    methods: {
        loadFirstBatchOfTransactions(period) {
            if (!this.transactionsByPeriod[period]) {
                this.$set(this.transactionsMetaByPeriod, period, {});
                this.$set(this.transactionsByPeriod, period, []);
                this.loadTransactions(period)
            }
        },
        loadNextBatchOfTransactions(period) {
            if (this.transactionsByPeriod[period]) {
                this.loadTransactions(period);
            }
        },
        loadTransactions(period) {
            const self = this;
            this.$set(self.transactionsMetaByPeriod[period], 'loading', true);
            this.$set(self.transactionsMetaByPeriod[period], 'error', false);
            axios.get(route('nl.employerdashboard.invoice.transactions-for-period', {
                company_as_uuid: this.$page.props.auth.user.selected_company.uuid,
                invoice: this.invoice.uuid,
                period: period,
                per_page: 50,
                page: Math.ceil(this.transactionsByPeriod[period].length / 50) + 1,
            })).then(data => {
                this.$set(self.transactionsByPeriod, period, self.transactionsByPeriod[period].concat(data.data.data));
                this.$set(self.transactionsMetaByPeriod[period], 'total', data.data.meta.total);
            }).catch(err => {
                console.error(err)
                this.$set(self.transactionsMetaByPeriod[period], 'error', true);
            }).finally(() => {
                this.$set(self.transactionsMetaByPeriod[period], 'loading', false);
            })
        }
    },
}
</script>

<style scoped>
    input + label > img {
        transition: transform 250ms ease-in-out;
        transform: rotate(-90deg);
    }

    input:checked + label > img {
        transform: rotate(90deg);
    }

    input + label ~ div {
        display: none;
    }

    input:checked + label ~ div {
        display: block;
    }
</style>
